import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/types/common';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry a client error — the request itself is the problem.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Query-key factory. Centralised so invalidation after a scan/generate stays
 * honest — no stringly-typed keys scattered through the features.
 */
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },
  /**
   * Every dashboard key carries the active date window, so switching the topbar
   * filter reads from cache on the way back instead of refetching — and two
   * windows can never overwrite each other's data.
   */
  dashboard: {
    all: ['dashboard'] as const,
    summary: (window?: unknown) => ['dashboard', 'summary', window ?? {}] as const,
    dailyActivity: (range: string, window?: unknown) =>
      ['dashboard', 'daily-activity', range, window ?? {}] as const,
    qrStatus: (window?: unknown) => ['dashboard', 'qr-status', window ?? {}] as const,
    buyerWise: (period: string, window?: unknown) =>
      ['dashboard', 'buyer-wise', period, window ?? {}] as const,
    recentActivities: (limit: number, window?: unknown) =>
      ['dashboard', 'activities', limit, window ?? {}] as const,
    poSummary: (limit: number, window?: unknown) =>
      ['dashboard', 'po-summary', limit, window ?? {}] as const,
    todaySummary: (window?: unknown) => ['dashboard', 'today', window ?? {}] as const,
  },
  master: {
    all: ['master'] as const,
    buyers: (params?: unknown) => ['master', 'buyers', params ?? {}] as const,
    purchaseOrders: (params?: unknown) => ['master', 'purchase-orders', params ?? {}] as const,
    styles: (poId: number | 'none') => ['master', 'styles', poId] as const,
    stylesPage: (params?: unknown) => ['master', 'styles-page', params ?? {}] as const,
    lotNumbers: (styleId: number | 'none') => ['master', 'lot-numbers', styleId] as const,
    lotNumbersPage: (params?: unknown) => ['master', 'lot-numbers-page', params ?? {}] as const,
    sizes: (lotNumberId: number | 'none') => ['master', 'sizes', lotNumberId] as const,
    sizesPage: (params?: unknown) => ['master', 'sizes-page', params ?? {}] as const,
    warehouses: ['master', 'warehouses'] as const,
    shipments: (poId?: number) => ['master', 'shipments', poId ?? 'all'] as const,
  },
  qr: {
    all: ['qr'] as const,
    list: (params?: unknown) => ['qr', 'list', params ?? {}] as const,
    batches: (params?: unknown) => ['qr', 'batches', params ?? {}] as const,
    byCode: (code: string) => ['qr', 'code', code] as const,
  },
  cartons: {
    all: ['cartons'] as const,
    list: (params?: unknown) => ['cartons', 'list', params ?? {}] as const,
    currentLot: ['cartons', 'current-lot'] as const,
    currentDispatchLot: ['cartons', 'current-dispatch-lot'] as const,
    receivedLots: (params?: unknown) => ['cartons', 'received-lots', params ?? {}] as const,
    dispatchedLots: (params?: unknown) => ['cartons', 'dispatched-lots', params ?? {}] as const,
    receipts: (params?: unknown) => ['cartons', 'receipts', params ?? {}] as const,
    dispatches: (params?: unknown) => ['cartons', 'dispatches', params ?? {}] as const,
    timeline: (code: string) => ['cartons', 'timeline', code] as const,
    scanHistory: (params?: unknown) => ['scan-history', params ?? {}] as const,
  },
  commercialInfo: {
    all: ['commercial-info'] as const,
    list: (params?: unknown) => ['commercial-info', 'list', params ?? {}] as const,
  },
  reports: {
    rows: (type: string, filters?: unknown) => ['reports', type, 'rows', filters ?? {}] as const,
    totals: (type: string, filters?: unknown) => ['reports', type, 'totals', filters ?? {}] as const,
  },
  users: {
    all: ['users'] as const,
    list: (params?: unknown) => ['users', 'list', params ?? {}] as const,
  },
  settings: ['settings'] as const,
  /** Platform-operator view of every workspace — only ever queried by the seeded admin. */
  tenants: {
    all: ['tenants'] as const,
    list: ['tenants', 'list'] as const,
  },
};

/**
 * Everything a receive/dispatch/generate action can affect. Called after each
 * mutation so counters, charts and tables refresh together.
 */
export function invalidateTrackingData() {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
    queryClient.invalidateQueries({ queryKey: queryKeys.qr.all }),
    queryClient.invalidateQueries({ queryKey: queryKeys.cartons.all }),
    queryClient.invalidateQueries({ queryKey: ['scan-history'] }),
    // A dispatch that completes a lot creates its commercial-info row.
    queryClient.invalidateQueries({ queryKey: queryKeys.commercialInfo.all }),
    queryClient.invalidateQueries({ queryKey: ['reports'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.master.all }),
  ]);
}
